import React, {useState, useMemo, useCallback, useEffect} from 'react';
import {
    Grid, Box, CardContent, CardActions, Stack,
    InputLabel, FormHelperText, FormControl, Select, MenuItem, Pagination,
    List, ListItem, ListItemAvatar, ListItemText, Avatar,
} from '@mui/material';
import { Breadcrumbs, GeneralCard } from '../../../../Components';
import { MonthSelector } from "./Elements/MonthSelector";
import {EmployeeCalendar} from "./Elements/EmployeeCalendar";
import {fetchRosterCalendar, setConfigCalendar} from "../Redux/Action";
import {setSelectedDepartment} from "../../Department/Redux/Action";
import {useDispatch, useSelector} from "react-redux";
import useDeepEqualSelector from "../../../../Hooks/useDeepEqualSelector";
import { usePermissions } from '../../../../Hooks/usePermissions';
import dayjs from 'dayjs';
import { styled, useTheme } from '@mui/material/styles';
import { getStatusColor } from '../../../../Setup';

/** Legend row + rounded code swatches (swatch fill via `--legend-swatch-bg` on each Avatar). */
const LegendList = styled(List)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: 0,
    '& .MuiListItem-root': {
        width: 'auto',
        padding: 0,
        gap: theme.spacing(1),
    },
    '& .MuiListItemAvatar-root': { minWidth: 0, marginTop: 0 },
    '& .MuiListItemText-root': { width: 'max-content', margin: 0 },
    '& .MuiListItemAvatar-root .MuiAvatar-root': {
        width: 32,
        height: 32,
        fontSize: '0.8rem',
        fontWeight: 700,
        color: '#333',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: 'var(--legend-swatch-bg)',
    },
}));

/** Matches EmployeeCalendar status → palette mapping + getCellColor(..., '0.8', 'lighten'). */
const ROSTER_CALENDAR_LEGEND = [
    { status: '24_HOURS', code: '24', label: '24 Hours', level: '0.8', effect: 'lighten' },
    { status: 'ON_DUTY', code: 'OD', label: 'On Duty', level: '0.8', effect: 'lighten' },
    { status: 'OFF', code: 'O', label: 'Off', level: '0.8', effect: 'lighten' },
    { status: 'ON_CALL', code: 'OC', label: 'On Call', level: '0.8', effect: 'lighten' },
    { status: 'ON_SITE', code: 'OS', label: 'On Site', level: '0.8', effect: 'lighten' },
    { status: 'HOLIDAY', code: 'H', label: 'Holiday', level: '0.8', effect: 'lighten' },
    { status: 'WEEKEND', code: 'W', label: 'Weekend', level: '0.3', effect: '' },
];

const RosterCalendarLegend = () => {
    const theme = useTheme();
    const statusColorMap = useMemo(
        () => ({
            ON_CALL: 'info',
            OFF: 'error',
            '24_HOURS': 'warning',
            ON_DUTY: 'primary',
            HOLIDAY: 'accent',
            WEEKEND: 'warning',
            ON_SITE: 'success',
        }),
        []
    );
    const getCellColor = useCallback(
        (status, level, effect) => {
            const colorName = statusColorMap[status];
            return getStatusColor(theme, colorName, level, effect);
        },
        [theme, statusColorMap]
    );

    return (
        <LegendList disablePadding>
            {ROSTER_CALENDAR_LEGEND.map((item) => (
                <ListItem key={item.status} dense disableGutters>
                    <ListItemAvatar>
                        <Avatar
                            variant="rounded"
                            style={{
                                '--legend-swatch-bg': getCellColor(item.status, item.level || '0.8', item.effect || 'lighten'),
                            }}
                        >
                            {item.code}
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                        primary={`${item.label}`}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    />
                </ListItem>
            ))}
        </LegendList>
    );
};

export const Calendar = () => {

    const dispatch = useDispatch();
    const memoizedMaxDate = useMemo(() => dayjs(), []);
    const {hasAnyPermission} = usePermissions();
    const user = useSelector(state => state.auth?.user) || {};
    const dirty = useSelector(state => state.rosters?.dirty) || false;
    const config = useSelector(state => state.rosters.calendar?.config) || {};
    const filtersRaw = useDeepEqualSelector(state => state.rosters.calendar?.config?.filters);
    const filters = useMemo(() => filtersRaw || {}, [filtersRaw]);
    const paginationRaw = useDeepEqualSelector(state => state.rosters.calendar?.config?.pagination);
    const pagination = useMemo(() => paginationRaw || {}, [paginationRaw]);
    const selectedDepartment = useSelector(state => state.departments?.selected) || null;
    /* eslint-disable no-unused-vars -- setters reserved for future use */
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('info');
    const [isLoading, setIsLoading] = useState(false);
    /* eslint-enable no-unused-vars */

    const canChangeDepartment = hasAnyPermission(['roster.roster.access-all', 'roster.department.access-all']);

    const fetchData = useCallback(async () => {
        // Filter out empty department_id
        const cleanFilters = { ...filters };
        const deptId = cleanFilters.department_id;
        
        // Remove department_id if it's null, undefined, empty string, or empty object {}
        if (deptId === null ||
            deptId === undefined ||
            deptId === '' ||
            deptId === 'all' ||
            (typeof deptId === 'object' && deptId !== null && Object.keys(deptId).length === 0)) {
            delete cleanFilters.department_id;
        }
        const payload = {
            ...cleanFilters,
            ...pagination,
            pageSize:9,
            sort_by: 'first_name',
            sort_dir: 'asc',
            columns:['id','first_name','last_name','department_id','avatar'],
            with: ['rosters.status','department'],
        };
        await fetchRosterCalendar({ dispatch, filters: payload });
    }, [dispatch, filters, pagination]);

    // First time load - always fetch
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Refresh when dirty changes
    useEffect(() => {
        if (dirty) {
            fetchData();
        }
    }, [dirty, fetchData]);

    // Lock department filter for users without access-all permission
    useEffect(() => {
        if (!canChangeDepartment) {
            const defaultDeptId = user?.department_id || '';
            if (filters.department_id !== defaultDeptId) {
                dispatch(setConfigCalendar({
                    filters: { ...filters, department_id: defaultDeptId },
                    pagination: { ...pagination, page: 0 }
                }));
            }
        }
    }, [canChangeDepartment, user?.department_id, dispatch, filters, pagination]);

    // Set department filter from selected department
    useEffect(() => {
        if (!canChangeDepartment) {
            return;
        }

        if (selectedDepartment) {
            const departmentId = selectedDepartment.id || selectedDepartment;
            if (filters.department_id !== departmentId) {
                dispatch(setConfigCalendar({
                    filters: { ...filters, department_id: departmentId },
                    pagination: { ...pagination, page: 0 }
                }));
            }
            return;
        }

        if (filters.department_id && filters.department_id !== 'all') {
            dispatch(setConfigCalendar({
                filters: { ...filters, department_id: 'all' },
                pagination: { ...pagination, page: 0 }
            }));
        }
    }, [selectedDepartment, canChangeDepartment, dispatch, filters, pagination]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        
        dispatch(setConfigCalendar({
            filters: {
                ...filters,
                [name]: name === 'month' ? dayjs(value).format('YYYY-MM') : value
            },
            pagination: {
                ...config.pagination,
                page: 0
            }
        }));
    }, [dispatch, filters, config.pagination]);
    
    const handleDepartmentChange = useCallback((e, departments) => {
        const { value } = e.target;

        if (value === 'all') {
            dispatch(setSelectedDepartment(null));
        } else {
            const selectedDept = departments.find((dept) => dept.id === value);
            if (selectedDept) {
                dispatch(setSelectedDepartment(selectedDept));
            }
        }

        handleChange(e);
    }, [dispatch, handleChange]);
    
    const handleChangePage = (event, newPage) => {
        dispatch(setConfigCalendar({ ...config, pagination: { ...config.pagination, page: newPage - 1 } }));
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Breadcrumbs items={['DASHBOARD','ROSTER','CALENDAR']} />
                    {/* {hasPermission('roster.roster.generate') && (
                        <Button variant="contained" color="primary" onClick={() => navigate('/roster/generate')}>Generate Roster</Button>
                    )} */}
                </Box>
            </Grid>

            <Grid item xs={12}>
                <GeneralCard>
                    <CardContent sx={{p:'0.75rem 0 0!important'}}>
                        <Filters
                            data={filters}
                            handleChange={handleChange}
                            handleDepartmentChange={handleDepartmentChange}
                            memoizedMaxDate={memoizedMaxDate}
                            errors={errors}
                            canChangeDepartment={canChangeDepartment}
                            dispatch={dispatch}
                        />
                        <EmployeeCalendar selectedMonth={dayjs(filters.month)} />
                    </CardContent>
                    <CardActions sx={{justifyContent: 'space-between'}}>
                            <RosterCalendarLegend />
                            <Pagination
                                count={config?.lastPage}
                                page={(config?.pagination?.page || 0) + 1}
                                onChange={handleChangePage}
                                showFirstButton
                                showLastButton
                                variant="outlined"
                                shape="rounded"
                                siblingCount={0}
                                boundaryCount={0}
                            />
                    </CardActions>
                </GeneralCard>
            </Grid>
        </Grid>
    );
};

export const Filters = ({ data, handleChange, handleDepartmentChange, memoizedMaxDate, errors, canChangeDepartment, dispatch }) => {

    const appConfig = useSelector(state => state.app.config);
    const [departments, setDepartments] = useState([]);

    useEffect(() => {
        if(appConfig){
            setDepartments(appConfig.departments || []);
        }
    }, [appConfig]);
    
    const onDepartmentChange = (e) => {
        handleDepartmentChange(e, departments);
    };

    return (
        <Stack direction={"row"} spacing={2} sx={{m:`0.5rem 1rem 1rem`}} justifyContent={"space-between"}>
            <Stack direction="row" spacing={2} alignItems="center">
                <MonthSelector
                    value={data.month}
                    onChange={handleChange}
                    disableFuture={false}
                    disablePast={false}
                />
            </Stack>

            {canChangeDepartment && (
                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl fullWidth sx={{minWidth:'200px'}} error={!!errors?.department_id}>
                        <InputLabel>Department</InputLabel>
                        <Select
                            name="department_id"
                            value={data.department_id || 'all'}
                            onChange={onDepartmentChange}
                            label="Department"
                            variant="outlined"
                        >
                            <MenuItem value="all" key="all">All Departments</MenuItem>
                            {departments.map((department) => (
                                <MenuItem key={department.id} value={department.id}>{department.department_name}</MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>{errors?.department_id || ''}</FormHelperText>
                    </FormControl>
                </Stack>
            )}
        </Stack>
    );
}